import { type ImageDimensionConstraints, normalizeImageDimensions } from './image-dimensions';

export const IMAGE_NODE_TYPE = 'image';

export function isEditorImageNodeType(nodeType: string) {
  return nodeType === IMAGE_NODE_TYPE;
}

export interface EditorImageNodeData {
  fileId: string;
  altText?: string;
  fileName?: string;
  width?: number;
  height?: number;
}

export interface EditorImageNodeContext extends EditorImageNodeData {
  nodeType: string;
}

export interface EditorImageDimensionDeps {
  dimensionConstraints?: ImageDimensionConstraints;
}

export interface EditorImageNodeReadDeps extends EditorImageDimensionDeps {
  readString?(value: Record<string, unknown>, key: string): string | undefined;
  readNumber?(value: Record<string, unknown>, key: string): number | undefined;
  getImageNodeData?(value: unknown): EditorImageNodeContext | undefined;
  visitEditorNodes?(value: unknown, visitor: (node: Record<string, unknown>) => void): void;
}

export function getEditorImageNodeData(
  value: unknown,
  deps: EditorImageNodeReadDeps = {},
): EditorImageNodeContext | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  const readString = deps.readString ?? defaultReadString;
  const readNumber = deps.readNumber ?? defaultReadNumber;
  const nodeType = readString(value, 'type');
  if (!nodeType || !isEditorImageNodeType(nodeType)) {
    return undefined;
  }

  const fileId = readString(value, 'fileId') ?? readString(value, 'file_id');
  if (!fileId) {
    return undefined;
  }

  return {
    nodeType,
    fileId,
    altText:
      readString(value, 'altText') ?? readString(value, 'alt') ?? readString(value, 'alt_text'),
    fileName: readString(value, 'fileName') ?? readString(value, 'file_name'),
    ...normalizeEditorImageDimensions(
      {
        width: readNumber(value, 'width'),
        height: readNumber(value, 'height'),
      },
      deps,
    ),
  };
}

export function normalizeEditorImageDimensions(
  { width, height }: { width?: number; height?: number },
  deps: EditorImageDimensionDeps = {},
) {
  return normalizeImageDimensions(width, height, deps.dimensionConstraints);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function defaultReadString(value: Record<string, unknown>, key: string): string | undefined {
  const target = value[key];
  return typeof target === 'string' && target.length > 0 ? target : undefined;
}

function defaultReadNumber(value: Record<string, unknown>, key: string): number | undefined {
  const target = value[key];
  return typeof target === 'number' && Number.isFinite(target) ? target : undefined;
}
